import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { format, addMonths, subMonths, startOfMonth, endOfMonth, startOfWeek, endOfWeek, eachDayOfInterval, isSameMonth, parseISO } from 'date-fns';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { Plus, Loader2, X, ChevronLeft, ChevronRight, MapPin, Briefcase, Coins, Calendar as CalendarIcon, Trash2, ArrowLeftRight } from 'lucide-react';
import MapPickerDialog from './MapPickerDialog';
import { useRoles } from '../hooks/useRoles';
import { cn } from '../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Same legacy mapping as ShiftsPage — kept local so both files evolve freely.
const LEGACY_ROLE_MAP = {
  'Cleaner': 'cleaner',
  'Dual role (SIA, cleaner)': 'sia_door_supervisor',
  'SIA Door Supervisor': 'sia_door_supervisor',
  'Steward (CSO)': 'cso_steward',
  'Event Supervisor': 'supervisor',
};
const roleNameToSlug = (name, activeRoles = []) => {
  if (!name) return null;
  if (LEGACY_ROLE_MAP[name]) return LEGACY_ROLE_MAP[name];
  const found = activeRoles.find((r) => r.name === name);
  return found ? found.slug : null;
};
const CURRENCY_OPTIONS = [
  { code: 'GBP', symbol: '£', label: 'GBP (£)' },
  { code: 'EUR', symbol: '€', label: 'EUR (€)' },
  { code: 'USD', symbol: '$', label: 'USD ($)' },
];
const currencySymbol = (code) => CURRENCY_OPTIONS.find((c) => c.code === code)?.symbol || '£';

const EMPTY_FORM = {
  start_time: '07:00', end_time: '19:00',
  site_name: '', postcode: '', location_link: '', geo_radius: '',
  shift_category: 'retail', shift_role: '', hourly_rate: '', currency: 'GBP',
  hourly_rate_breakdown: { sia_door_supervisor: 0, cso_steward: 0, cleaner: 0, supervisor: 0 },
};

// Full-power "Add Shift" for one employee (Employee Profile → Schedule).
// Mirrors the Retail Calendar form: multi-date calendar picker, saved site
// library, geofence link, category, role + hourly rate. Days where the
// employee ALREADY has a shift are blocked for new shifts but clickable to
// reassign the shift to a free colleague or delete it entirely.
export default function AddShiftForEmployee({ employeeId, onCreated }) {
  const { activeRoles, rolesBySlug } = useRoles();
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sites, setSites] = useState([]);
  const [siteListOpen, setSiteListOpen] = useState(false);
  const [mapOpen, setMapOpen] = useState(false);
  const [month, setMonth] = useState(new Date());
  const [selectedDates, setSelectedDates] = useState([]);
  const [myShifts, setMyShifts] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  // Blocked-day management panel — multi-select days with existing shifts
  const [manageDays, setManageDays] = useState([]); // ['yyyy-MM-dd', ...]
  const [candidatesByDate, setCandidatesByDate] = useState({}); // date -> list | null (loading)
  const [bulkSel, setBulkSel] = useState('');
  const [reassignSel, setReassignSel] = useState({}); // shiftId -> employeeId
  const [actionBusy, setActionBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const fetchMyShifts = () =>
    axios.get(`${API}/shifts`, { params: { employee_id: employeeId } })
      .then((r) => setMyShifts((r.data || []).filter((s) => s.status !== 'cancelled')))
      .catch(() => setMyShifts([]));

  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/admin/retail-sites`).then((r) => setSites(r.data || [])).catch(() => {});
    fetchMyShifts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const shiftsByDate = useMemo(() => {
    const map = {};
    myShifts.forEach((s) => {
      if (!s.date) return;
      (map[s.date] = map[s.date] || []).push(s);
    });
    return map;
  }, [myShifts]);

  const calendarDays = useMemo(() => eachDayOfInterval({
    start: startOfWeek(startOfMonth(month)),
    end: endOfWeek(endOfMonth(month)),
  }), [month]);

  const toggleDate = (dateStr) =>
    setSelectedDates((prev) => (prev.includes(dateStr) ? prev.filter((d) => d !== dateStr) : [...prev, dateStr].sort()));

  const toggleManageDay = (dateStr) => {
    setBulkSel('');
    setManageDays((prev) =>
      prev.includes(dateStr) ? prev.filter((d) => d !== dateStr) : [...prev, dateStr].sort());
    if (candidatesByDate[dateStr] === undefined) {
      const first = (shiftsByDate[dateStr] || [])[0];
      if (!first) return;
      setCandidatesByDate((p) => ({ ...p, [dateStr]: null }));
      axios.get(`${API}/admin/shifts/${first.id}/reassign-candidates`)
        .then((r) => setCandidatesByDate((p) => ({ ...p, [dateStr]: r.data || [] })))
        .catch(() => setCandidatesByDate((p) => ({ ...p, [dateStr]: [] })));
    }
  };

  const afterAction = () => {
    setManageDays([]);
    setCandidatesByDate({});
    setBulkSel('');
    setReassignSel({});
    fetchMyShifts();
    onCreated?.();
  };

  // Colleagues free on EVERY selected day (null while any list still loads).
  const commonCandidates = useMemo(() => {
    if (manageDays.length === 0) return [];
    const lists = manageDays.map((d) => candidatesByDate[d]);
    if (lists.some((l) => l == null)) return null;
    let common = lists[0];
    for (const l of lists.slice(1)) {
      const ids = new Set(l.map((c) => c.id));
      common = common.filter((c) => ids.has(c.id));
    }
    return common;
  }, [manageDays, candidatesByDate]);

  const manageShiftsMovable = manageDays
    .flatMap((d) => shiftsByDate[d] || [])
    .filter((s) => s.status === 'scheduled');

  const bulkMove = async () => {
    if (!bulkSel) { toast.error('Pick a colleague first'); return; }
    const target = (commonCandidates || []).find((c) => c.id === bulkSel);
    if (!window.confirm(`Move ${manageShiftsMovable.length} shift(s) across ${manageDays.length} day(s) to ${target?.name || 'this colleague'}?`)) return;
    const notify = window.confirm('Send email notification to both employees?\n\nOK = send emails now\nCancel = move WITHOUT sending any email');
    setActionBusy(true);
    let moved = 0;
    const failed = [];
    for (const s of manageShiftsMovable) {
      try {
        await axios.post(`${API}/admin/shifts/${s.id}/reassign`, { new_employee_id: bulkSel, notify });
        moved += 1;
      } catch (e) {
        failed.push(`${s.date}: ${e.response?.data?.detail || 'failed'}`);
      }
    }
    setActionBusy(false);
    if (moved > 0) toast.success(`${moved} shift(s) moved to ${target?.name || 'colleague'}`);
    if (failed.length > 0) toast.error(`Could not move ${failed.length} shift(s) — ${failed[0]}`);
    afterAction();
  };

  const reassign = async (shift) => {
    const targetId = reassignSel[shift.id];
    if (!targetId) { toast.error('Pick a colleague first'); return; }
    const dayCandidates = candidatesByDate[shift.date] || [];
    const target = dayCandidates.find((c) => c.id === targetId);
    if (!window.confirm(`Move the ${shift.date} shift at ${shift.site_name || 'site'} to ${target?.name || 'this colleague'}?`)) return;
    const notify = window.confirm('Send email notification to both employees?\n\nOK = send emails now\nCancel = move WITHOUT sending any email');
    setActionBusy(true);
    try {
      const r = await axios.post(`${API}/admin/shifts/${shift.id}/reassign`, { new_employee_id: targetId, notify });
      toast.success(`Shift moved to ${r.data?.new_employee}${r.data?.emails_sent ? ` · ${r.data.emails_sent} email(s) sent` : ''}`);
      afterAction();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to move shift');
    } finally {
      setActionBusy(false);
    }
  };

  const deleteShift = async (shift) => {
    if (!window.confirm(`Delete the ${shift.date} shift at ${shift.site_name || 'site'} completely?`)) return;
    const notify = window.confirm('Send email notification to the employee?\n\nOK = send email now\nCancel = delete WITHOUT sending any email');
    setActionBusy(true);
    try {
      const r = await axios.delete(`${API}/shifts/${shift.id}?notify=${notify}`);
      toast.success(`Shift deleted${r.data?.emails_sent ? ' · email sent' : ''}`);
      afterAction();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete shift');
    } finally {
      setActionBusy(false);
    }
  };

  const save = async () => {
    if (selectedDates.length === 0) { toast.error('Select at least one date on the calendar'); return; }
    if (!form.site_name.trim()) { toast.error('Site name is required'); return; }
    setSaving(true);
    try {
      const roleKey = roleNameToSlug(form.shift_role, activeRoles);
      const typedRate = form.hourly_rate === '' ? 0 : Math.max(0, parseFloat(form.hourly_rate) || 0);
      const rateBd = { ...form.hourly_rate_breakdown };
      if (roleKey) rateBd[roleKey] = typedRate;
      const notify = window.confirm('Send email notification to the employee?\n\nOK = send email now\nCancel = save WITHOUT sending any email');
      const res = await axios.post(`${API}/shifts/batch-standalone?notify=${notify}`, {
        employee_ids: [employeeId],
        dates: selectedDates,
        start_time: form.start_time,
        end_time: form.end_time,
        site_name: form.site_name.trim(),
        postcode: form.postcode.trim(),
        shift_role: form.shift_role,
        shift_category: form.shift_category,
        hourly_rate_breakdown: rateBd,
        currency: form.currency || 'GBP',
        location_link: form.location_link || null,
        geo_radius: form.location_link && form.geo_radius ? parseInt(form.geo_radius) : null,
        notes: '',
      });
      const created = res.data?.created || 0;
      const skipped = res.data?.skipped || 0;
      if (created > 0) {
        toast.success(`${created} shift(s) added${skipped ? ` · ${skipped} skipped (overlap)` : ''}${res.data?.emails_sent ? ' · email sent' : ''}`);
        axios.post(`${API}/admin/retail-sites/upsert`, {
          site_name: form.site_name.trim(),
          postcode: form.postcode.trim(),
          location_link: (form.location_link || '').trim() || null,
          geo_radius: (form.location_link || '').trim() && form.geo_radius ? parseInt(form.geo_radius) : null,
        }).catch(() => {});
        setOpen(false);
        setSelectedDates([]);
        setManageDays([]);
        onCreated?.();
      } else {
        const d = res.data?.skip_details || [];
        toast.error(d[0] || 'All shifts were skipped (possible overlap)');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add shifts');
    } finally {
      setSaving(false);
    }
  };

  const renderSiteSuggestions = () => {
    if (!siteListOpen || sites.length === 0) return null;
    const q = (form.site_name || '').toLowerCase();
    const list = sites.filter((s) => !q || s.site_name.toLowerCase().includes(q));
    if (list.length === 0) return null;
    return (
      <div className="absolute z-50 left-0 right-0 top-full mt-1 max-h-48 overflow-y-auto border border-border rounded-sm bg-popover shadow-lg" data-testid="emp-site-suggestions">
        {list.map((s) => (
          <button
            key={s.id}
            type="button"
            onMouseDown={(e) => {
              e.preventDefault();
              setForm((f) => ({
                ...f,
                site_name: s.site_name,
                postcode: s.postcode || f.postcode,
                location_link: s.location_link || f.location_link,
                geo_radius: s.location_link
                  ? (s.geo_radius != null ? String(s.geo_radius) : '')
                  : f.geo_radius,
              }));
              setSiteListOpen(false);
            }}
            className="w-full text-left px-3 py-2 text-sm hover:bg-muted flex items-center justify-between gap-2"
            data-testid={`emp-site-option-${s.id}`}
          >
            <span className="truncate">{s.site_name}</span>
            <span className="flex items-center gap-1.5 shrink-0">
              {s.location_link && <MapPin className="w-3 h-3 text-emerald-400" title="Saved clock-in location" />}
              <span className="text-xs text-muted-foreground">{s.postcode}</span>
            </span>
          </button>
        ))}
      </div>
    );
  };

  const dayPanelShifts = (d) => shiftsByDate[d] || [];

  return (
    <>
      <Button
        size="sm"
        onClick={() => setOpen(true)}
        className="rounded-sm h-8 bg-primary text-primary-foreground hover:bg-yellow-300"
        data-testid="add-shift-for-employee-btn"
      >
        <Plus className="w-3.5 h-3.5 mr-1.5" /> Add Shift
      </Button>
      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setManageDays([]); }}>
        <DialogContent className="bg-card border-border rounded-sm max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Shifts for this Employee</DialogTitle>
            <DialogDescription>
              Click free days to select them. Days with an existing shift are blocked — tick one or more to move them to a colleague or delete them.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* Calendar picker */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                  Select Dates ({selectedDates.length} selected)
                </Label>
                <div className="flex items-center gap-1">
                  <Button type="button" variant="ghost" size="icon" onClick={() => setMonth(subMonths(month, 1))} className="h-6 w-6" data-testid="emp-cal-prev">
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="text-xs font-medium min-w-[100px] text-center">{format(month, 'MMM yyyy')}</span>
                  <Button type="button" variant="ghost" size="icon" onClick={() => setMonth(addMonths(month, 1))} className="h-6 w-6" data-testid="emp-cal-next">
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <div className="bg-background border border-border rounded-sm p-2">
                <div className="grid grid-cols-7 gap-1 mb-1">
                  {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((d) => (
                    <div key={d} className="text-center text-[10px] text-muted-foreground py-1">{d}</div>
                  ))}
                </div>
                <div className="grid grid-cols-7 gap-1">
                  {calendarDays.map((day) => {
                    const dateStr = format(day, 'yyyy-MM-dd');
                    const dayShifts = shiftsByDate[dateStr];
                    const blocked = !!dayShifts;
                    const isSelected = selectedDates.includes(dateStr);
                    const isCurrentMonth = isSameMonth(day, month);
                    const isToday = dateStr === format(new Date(), 'yyyy-MM-dd');
                    return (
                      <button
                        key={dateStr}
                        type="button"
                        onClick={() => (blocked ? toggleManageDay(dateStr) : toggleDate(dateStr))}
                        className={cn(
                          'aspect-square flex flex-col items-center justify-center text-xs rounded-sm transition-all leading-none gap-0.5',
                          blocked
                            ? 'bg-red-500/15 border border-red-500/50 text-red-500 font-semibold hover:bg-red-500/25 cursor-pointer'
                            : isSelected
                              ? 'bg-primary text-black font-bold'
                              : 'hover:bg-accent',
                          !isCurrentMonth && 'opacity-30',
                          isToday && !isSelected && !blocked && 'border border-primary/50',
                          manageDays.includes(dateStr) && 'ring-2 ring-red-500'
                        )}
                        data-testid={blocked ? `emp-day-blocked-${dateStr}` : `emp-day-${dateStr}`}
                      >
                        {format(day, 'd')}
                        {blocked && <span className="text-[8px] font-normal">{(dayShifts[0].start_time || 'TBC').slice(0, 5)}</span>}
                      </button>
                    );
                  })}
                </div>
                <p className="text-[10px] text-muted-foreground mt-2 flex items-center gap-3">
                  <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-primary inline-block" /> Selected for new shift</span>
                  <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-red-500/40 border border-red-500 inline-block" /> Existing shift — tick one or more to manage</span>
                </p>
              </div>
              {selectedDates.length > 0 && (
                <div className="flex flex-wrap gap-1.5" data-testid="emp-dates-chips">
                  {selectedDates.map((d) => (
                    <Badge key={d} variant="outline" className="rounded-sm text-[11px] gap-1">
                      {format(parseISO(d), 'MMM d')}
                      <X className="w-3 h-3 cursor-pointer hover:text-red-400" onClick={() => toggleDate(d)} />
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            {/* Blocked days management panel (multi-select) */}
            {manageDays.length > 0 && (
              <div className="border border-red-500/40 bg-red-500/5 rounded-sm p-3 space-y-3" data-testid="emp-day-panel">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold text-red-500">
                    Existing shifts — {manageDays.length} day{manageDays.length > 1 ? 's' : ''} selected
                  </p>
                  <Button type="button" variant="ghost" size="icon" className="h-6 w-6" onClick={() => { setManageDays([]); setBulkSel(''); }} data-testid="emp-day-panel-close">
                    <X className="w-4 h-4" />
                  </Button>
                </div>

                {/* Bulk move — one colleague for ALL ticked days */}
                {manageDays.length > 1 && (
                  <div className="bg-background border border-red-500/30 rounded-sm p-3 space-y-2" data-testid="emp-bulk-move-panel">
                    <p className="text-xs font-semibold uppercase tracking-wider text-red-400">
                      Move all {manageShiftsMovable.length} shift{manageShiftsMovable.length !== 1 ? 's' : ''} to one colleague
                    </p>
                    <div className="flex gap-2 items-center">
                      <Select value={bulkSel} onValueChange={setBulkSel}>
                        <SelectTrigger className="rounded-sm h-8 flex-1 text-xs" data-testid="emp-bulk-move-select">
                          <SelectValue placeholder={commonCandidates === null
                            ? 'Loading colleagues…'
                            : `Free on ALL selected days (${(commonCandidates || []).length})`} />
                        </SelectTrigger>
                        <SelectContent className="bg-card border-border max-h-56">
                          {commonCandidates !== null && (commonCandidates || []).length === 0 && (
                            <div className="px-3 py-2 text-xs text-muted-foreground">No colleague is free on every selected day</div>
                          )}
                          {(commonCandidates || []).map((c) => (
                            <SelectItem key={c.id} value={c.id}>{c.name}{c.role === 'supervisor' ? ' (Supervisor)' : ''}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button
                        type="button" size="sm" disabled={actionBusy || !bulkSel}
                        onClick={bulkMove}
                        className="rounded-sm h-8 text-xs bg-red-500 hover:bg-red-600 text-white"
                        data-testid="emp-bulk-move-btn"
                      >
                        <ArrowLeftRight className="w-3.5 h-3.5 mr-1" /> Move all
                      </Button>
                    </div>
                    <p className="text-[10px] text-muted-foreground">Only colleagues free on EVERY ticked day are listed. Tick/untick red days on the calendar to adjust.</p>
                  </div>
                )}

                {manageDays.map((d) => (
                  <div key={d} className="space-y-2">
                    <p className="text-xs font-semibold text-red-400/90">{format(parseISO(d), 'EEE, MMM d yyyy')}</p>
                    {dayPanelShifts(d).map((s) => (
                      <div key={s.id} className="bg-background border border-border rounded-sm p-3 space-y-2">
                        <div className="flex items-center justify-between gap-2 text-sm">
                          <div className="min-w-0">
                            <p className="font-medium truncate">{s.site_name || s.location || 'Shift'}</p>
                            <p className="text-xs text-muted-foreground">
                              {(s.start_time || 'TBC')} – {(s.end_time || 'TBC')}{s.shift_role ? ` · ${s.shift_role}` : ''}
                            </p>
                          </div>
                          <Badge variant="outline" className="rounded-sm text-[10px] uppercase shrink-0">{s.status}</Badge>
                        </div>
                        {s.status === 'scheduled' ? (
                          <div className="space-y-2">
                            <div className="flex gap-2 items-center">
                              <Select value={reassignSel[s.id] || ''} onValueChange={(v) => setReassignSel((p) => ({ ...p, [s.id]: v }))}>
                                <SelectTrigger className="rounded-sm h-8 flex-1 text-xs" data-testid={`reassign-select-${s.id}`}>
                                  <SelectValue placeholder={candidatesByDate[s.date] == null ? 'Loading colleagues…' : `Move to colleague (${(candidatesByDate[s.date] || []).length} free)`} />
                                </SelectTrigger>
                                <SelectContent className="bg-card border-border max-h-56">
                                  {candidatesByDate[s.date] != null && (candidatesByDate[s.date] || []).length === 0 && (
                                    <div className="px-3 py-2 text-xs text-muted-foreground">No free colleagues on this date</div>
                                  )}
                                  {(candidatesByDate[s.date] || []).map((c) => (
                                    <SelectItem key={c.id} value={c.id}>{c.name}{c.role === 'supervisor' ? ' (Supervisor)' : ''}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <Button
                                type="button" size="sm" variant="outline" disabled={actionBusy || !reassignSel[s.id]}
                                onClick={() => reassign(s)}
                                className="rounded-sm h-8 text-xs"
                                data-testid={`reassign-btn-${s.id}`}
                              >
                                <ArrowLeftRight className="w-3.5 h-3.5 mr-1" /> Move
                              </Button>
                            </div>
                            <p className="text-[10px] text-muted-foreground">Colleagues who already work this day are hidden from the list.</p>
                            <Button
                              type="button" size="sm" variant="destructive" disabled={actionBusy}
                              onClick={() => deleteShift(s)}
                              className="rounded-sm h-8 text-xs w-full"
                              data-testid={`delete-shift-btn-${s.id}`}
                            >
                              <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete this shift
                            </Button>
                          </div>
                        ) : (
                          <p className="text-[11px] text-muted-foreground">This shift is {s.status} — it cannot be moved or deleted from here.</p>
                        )}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}

            {/* Times */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Start Time</Label>
                <Input type="time" value={form.start_time} onChange={(e) => set('start_time', e.target.value)} className="rounded-sm h-9 bg-background" data-testid="add-shift-start" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">End Time</Label>
                <Input type="time" value={form.end_time} onChange={(e) => set('end_time', e.target.value)} className="rounded-sm h-9 bg-background" data-testid="add-shift-end" />
              </div>
            </div>

            {/* Site + postcode with saved sites library */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="relative">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Site Name</Label>
                <Input
                  value={form.site_name}
                  onChange={(e) => { set('site_name', e.target.value); setSiteListOpen(true); }}
                  onFocus={() => setSiteListOpen(true)}
                  onBlur={() => setTimeout(() => setSiteListOpen(false), 150)}
                  autoComplete="off"
                  placeholder="e.g., Main Office"
                  className="rounded-sm h-9 bg-background"
                  data-testid="add-shift-site"
                />
                {renderSiteSuggestions()}
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Postcode</Label>
                <Input
                  value={form.postcode}
                  onChange={(e) => set('postcode', e.target.value)}
                  placeholder="e.g., SW1A 1AA"
                  className="rounded-sm h-9 bg-background"
                  data-testid="add-shift-postcode"
                />
              </div>
            </div>

            {/* Geofence */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 border border-border rounded-sm p-3 bg-muted/40">
              <div className="sm:col-span-2">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Clock-in location link (optional)</Label>
                <Input
                  value={form.location_link}
                  onChange={(e) => set('location_link', e.target.value)}
                  placeholder="Paste Google Maps or what3words link"
                  className="rounded-sm h-9 bg-background"
                  data-testid="add-shift-location-link"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setMapOpen(true)}
                  className="h-8 rounded-sm text-xs w-full sm:w-auto mt-2"
                  data-testid="emp-pick-on-map-btn"
                >
                  <MapPin className="w-3.5 h-3.5 mr-1.5 text-emerald-400" /> Pick on map
                </Button>
                <MapPickerDialog
                  open={mapOpen}
                  onOpenChange={setMapOpen}
                  initialLink={form.location_link}
                  postcode={form.postcode}
                  siteName={form.site_name}
                  onPick={(link) => set('location_link', link)}
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Allowed distance</Label>
                <select
                  value={form.geo_radius}
                  onChange={(e) => set('geo_radius', e.target.value)}
                  className="w-full h-9 bg-background border border-border rounded-sm px-2 text-sm"
                  data-testid="add-shift-geo-radius"
                >
                  <option value="">No limit</option>
                  <option value="25">25 m</option>
                  <option value="50">50 m</option>
                  <option value="100">100 m</option>
                  <option value="200">200 m</option>
                  <option value="500">500 m</option>
                </select>
                <p className="text-[10px] text-muted-foreground mt-1">Staff outside this radius CANNOT clock in.</p>
              </div>
            </div>

            {/* Category */}
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1">
                <CalendarIcon className="w-3 h-3" /> Shift Category
              </Label>
              <Select value={form.shift_category} onValueChange={(v) => set('shift_category', v)}>
                <SelectTrigger className="rounded-sm h-9 bg-background" data-testid="add-shift-category"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-card border-border">
                  <SelectItem value="retail">Retail</SelectItem>
                  <SelectItem value="event">Event / Festival</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Role */}
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1">
                <Briefcase className="w-3 h-3" /> Role
              </Label>
              <Select value={form.shift_role || 'none'} onValueChange={(v) => {
                const val = v === 'none' ? '' : v;
                const key = roleNameToSlug(val, activeRoles);
                const stored = key ? Number(form.hourly_rate_breakdown?.[key]) || 0 : 0;
                const roleDefault = (key && rolesBySlug[key]) ? Number(rolesBySlug[key].default_hourly_rate) || 0 : 0;
                const roleCurrency = (key && rolesBySlug[key]) ? rolesBySlug[key].default_currency : form.currency;
                setForm((f) => ({
                  ...f,
                  shift_role: val,
                  hourly_rate: stored > 0 ? String(stored) : (roleDefault > 0 ? String(roleDefault) : ''),
                  currency: f.currency || roleCurrency || 'GBP',
                }));
              }}>
                <SelectTrigger className="rounded-sm h-9 bg-background" data-testid="add-shift-role"><SelectValue placeholder="Select role..." /></SelectTrigger>
                <SelectContent className="bg-card border-border">
                  <SelectItem value="none">— No role —</SelectItem>
                  {(activeRoles || []).map((r) => <SelectItem key={r.slug || r.name} value={r.name}>{r.name}</SelectItem>)}
                  <SelectItem value="Dual role (SIA, cleaner)">Dual role (SIA, cleaner)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Hourly rate + currency */}
            <div className="space-y-2 p-3 bg-muted/50 border border-border rounded-sm">
              <div className="flex items-center justify-between gap-2">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-2">
                  <Coins className="w-4 h-4" /> Hourly Rate
                </Label>
                <Select value={form.currency || 'GBP'} onValueChange={(v) => set('currency', v)}>
                  <SelectTrigger className="h-7 min-w-[110px] bg-background border-border rounded-sm text-xs" data-testid="add-shift-currency">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    {CURRENCY_OPTIONS.map((c) => <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-[70px_1fr] gap-2 items-center">
                <span className="text-lg text-muted-foreground tabular-nums">{currencySymbol(form.currency)}/hr</span>
                <Input
                  type="number" min="0" step="0.01" placeholder="0.00"
                  disabled={!roleNameToSlug(form.shift_role, activeRoles)}
                  value={form.hourly_rate}
                  onChange={(e) => set('hourly_rate', e.target.value)}
                  className="rounded-sm h-9 bg-background"
                  data-testid="add-shift-rate"
                />
              </div>
              <p className="text-[10px] text-muted-foreground">Select a role above to enable the rate for that role.</p>
            </div>

            {form.postcode && (
              <p className="text-xs text-muted-foreground">
                <MapPin className="w-3 h-3 inline mr-1" /> Site will be saved to the library with this postcode.
              </p>
            )}

            <Button disabled={saving} onClick={save} className="w-full rounded-sm bg-primary text-primary-foreground hover:bg-yellow-300 uppercase tracking-wider" data-testid="add-shift-save">
              {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</> : `Create ${selectedDates.length || ''} Shift${selectedDates.length !== 1 ? 's' : ''}`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
